import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import integrationService from '../services/integrationService'
import HealthStatusCard from '../components/integrations/HealthStatusCard'
import HealthHistoryTable, { type HealthHistoryFilters } from '../components/integrations/HealthHistoryTable'
import type { HealthStatus } from '../types/integration'

const IntegrationHealthPage: React.FC = () => {
  const autoRefresh = true
  const [activeTab, setActiveTab] = useState(0)
  const [historyFilters, setHistoryFilters] = useState<HealthHistoryFilters>({
    page: 0,
    page_size: 20
  })

  // Fetch health data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['integration-health'],
    queryFn: () => integrationService.getAllHealth(),
    refetchInterval: autoRefresh ? 60000 : false, // Auto-refresh every 60 seconds
    staleTime: 30000 // Consider data stale after 30 seconds
  })

  // Fetch health history data
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['integration-health-history', historyFilters],
    queryFn: async () => {
      // Mock data for now - replace with actual API call when backend endpoint is ready
      // return integrationService.getHealthHistory(historyFilters)
      return {
        records: data?.integrations.map((integration, index) => ({
          id: `${integration.service_type}-${integration.provider_name}-${index}`,
          service_type: integration.service_type,
          provider_name: integration.provider_name,
          status: integration.status,
          response_time_ms: integration.response_time_ms,
          checked_at: integration.last_check,
          error_message: integration.error_message,
          error_details: integration.status === 'error' ? { error: integration.error_message } : undefined
        })) || [],
        total: data?.integrations.length || 0
      }
    },
    enabled: activeTab === 1 && !!data
  })

  // Manual refresh handler
  const handleManualRefresh = () => {
    refetch()
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleHistoryFilterChange = (filters: HealthHistoryFilters) => {
    setHistoryFilters(filters)
  }

  // Calculate average response time
  const calculateAverageResponseTime = (integrations: HealthStatus[]): number => {
    const activeIntegrations = integrations.filter(
      (i) => i.status === 'active' && i.response_time_ms !== undefined
    )
    if (activeIntegrations.length === 0) return 0

    const total = activeIntegrations.reduce(
      (sum, i) => sum + (i.response_time_ms || 0),
      0
    )
    return Math.round(total / activeIntegrations.length)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load integration health data. Please try again.
        </Alert>
      </Box>
    )
  }

  const integrations = data?.integrations || []
  const totalCount = data?.total || 0
  const healthyCount = data?.healthy_count || 0
  const errorCount = data?.error_count || 0
  const avgResponseTime = calculateAverageResponseTime(integrations)

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Integration Health Monitor
        </Typography>
        <Tooltip title="Refresh health status">
          <IconButton onClick={handleManualRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
          <Tab icon={<HistoryIcon />} label="History" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Dashboard Tab */}
      {activeTab === 0 && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Services
                  </Typography>
                  <Typography variant="h3" component="div">
                    {totalCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Services
                  </Typography>
                  <Typography variant="h3" component="div" color="success.main">
                    {healthyCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Error Services
                  </Typography>
                  <Typography variant="h3" component="div" color="error.main">
                    {errorCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Response Time
                  </Typography>
                  <Typography variant="h3" component="div">
                    {avgResponseTime}
                    <Typography variant="caption" component="span" ml={1}>
                      ms
                    </Typography>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Auto-refresh indicator */}
          <Box mb={2}>
            <Alert severity="info" icon={<RefreshIcon />}>
              Auto-refreshing every 60 seconds. Last updated:{' '}
              {new Date().toLocaleTimeString()}
            </Alert>
          </Box>

          {/* Health Status Grid */}
          <Grid container spacing={3}>
            {integrations.map((integration) => (
              <Grid item xs={12} sm={6} md={4} key={`${integration.service_type}-${integration.provider_name}`}>
                <HealthStatusCard
                  integration={integration}
                  integrationId={integration.service_type + '-' + integration.provider_name}
                  onCheckComplete={() => refetch()}
                />
              </Grid>
            ))}
          </Grid>

          {integrations.length === 0 && (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="textSecondary">
                No integrations configured
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                Configure integrations to see their health status here
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 1 && (
        <>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <HealthHistoryTable
              records={historyData?.records || []}
              totalRecords={historyData?.total || 0}
              onFilterChange={handleHistoryFilterChange}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default IntegrationHealthPage
